import { useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { articlesApi } from '../../api/articlesApi';
import { articleCategoriesApi } from '../../api/contentApi';
import { Button } from '../../components/ui/Button';
import { MediaPicker } from '../../components/admin/MediaPicker';
import { RichTextEditor } from '../../components/admin/RichTextEditor';
import { ArticleCategoriesManager } from './ArticleCategoriesManager';

const API_URL = process.env.REACT_APP_API_URL;

const VIDE = {
  titre: '',
  slug: '',
  extrait: '',
  image_couverture: '',
  categorie_id: '',
  contenu: '',
  statut: 'brouillon',
  date_publication: '',
};

export function ArticlesManager() {
  const { data: articles, loading, refetch } = useFetch(() => articlesApi.adminList(), []);
  const { data: categories } = useFetch(() => articleCategoriesApi.list(), []);
  const [edition, setEdition] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [ongletActif, setOngletActif] = useState('articles');

  function ouvrirCreation() {
    setEdition({ ...VIDE });
    setErrors({});
  }

  function ouvrirEdition(article) {
    setEdition({ ...VIDE, ...article });
    setErrors({});
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});

    try {
      const payload = { ...edition, categorie_id: edition.categorie_id || null };
      if (edition.id) {
        await articlesApi.update(edition.id, payload);
      } else {
        await articlesApi.create(payload);
      }
      setEdition(null);
      refetch();
    } catch (err) {
      setErrors(err.errors || { _global: err.message });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Supprimer cet article ?')) return;
    await articlesApi.remove(id);
    refetch();
  }

  return (
    <section>
      <div className="admin-toolbar">
        <h1>Actualités & réalisations</h1>
        {ongletActif === 'articles' && !edition && <Button onClick={ouvrirCreation}>+ Nouvel article</Button>}
      </div>

      <div className="admin-tabs">
        <button
          type="button"
          className={`admin-tabs__item ${ongletActif === 'articles' ? 'admin-tabs__item--active' : ''}`}
          onClick={() => setOngletActif('articles')}
        >
          Articles
        </button>
        <button
          type="button"
          className={`admin-tabs__item ${ongletActif === 'categories' ? 'admin-tabs__item--active' : ''}`}
          onClick={() => setOngletActif('categories')}
        >
          Catégories
        </button>
      </div>

      {ongletActif === 'categories' && <ArticleCategoriesManager />}

      {ongletActif === 'articles' && loading && <p>Chargement...</p>}

      {ongletActif === 'articles' && !loading && edition && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="admin-form__field">
            <label>Titre</label>
            <input value={edition.titre} onChange={(e) => setEdition({ ...edition, titre: e.target.value })} required />
            {errors.titre && <span className="admin-form__error">{errors.titre}</span>}
          </div>

          <div className="admin-form__field">
            <label>Slug (laisser vide pour génération automatique)</label>
            <input value={edition.slug || ''} onChange={(e) => setEdition({ ...edition, slug: e.target.value })} />
          </div>

          <div className="admin-form__field">
            <label>Extrait</label>
            <input value={edition.extrait || ''} onChange={(e) => setEdition({ ...edition, extrait: e.target.value })} />
          </div>

          <MediaPicker
            label="Image de couverture"
            value={edition.image_couverture}
            onChange={(media) => setEdition({ ...edition, image_couverture: media?.chemin || '' })}
          />
          <p className="admin-form__hint">
            Cette image n'apparaît qu'en couverture (carte, en-tête d'article) — pour illustrer le corps du
            texte, insérez des images directement dans l'éditeur ci-dessous.
          </p>

          <div className="admin-form__field">
            <label>Catégorie</label>
            <select
              value={edition.categorie_id || ''}
              onChange={(e) => setEdition({ ...edition, categorie_id: e.target.value })}
            >
              <option value="">Aucune</option>
              {categories?.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nom}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-form__field">
            <label>Contenu</label>
            <RichTextEditor
              value={edition.contenu}
              onChange={(html) => setEdition({ ...edition, contenu: html })}
            />
            {errors.contenu && <span className="admin-form__error">{errors.contenu}</span>}
          </div>

          <div className="admin-form__field">
            <label>Statut</label>
            <select value={edition.statut} onChange={(e) => setEdition({ ...edition, statut: e.target.value })}>
              <option value="brouillon">Brouillon</option>
              <option value="publie">Publié</option>
              <option value="programme">Programmé</option>
            </select>
          </div>

          {edition.statut === 'programme' && (
            <div className="admin-form__field">
              <label>Date de publication</label>
              <input
                type="datetime-local"
                value={edition.date_publication || ''}
                onChange={(e) => setEdition({ ...edition, date_publication: e.target.value })}
              />
              {errors.date_publication && <span className="admin-form__error">{errors.date_publication}</span>}
            </div>
          )}

          {errors._global && <p className="admin-form__error">{errors._global}</p>}

          <div className="admin-form__actions">
            <Button type="submit" disabled={submitting}>
              {edition.id ? 'Enregistrer' : 'Créer'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEdition(null)}>
              Annuler
            </Button>
          </div>
        </form>
      )}

      {ongletActif === 'articles' && !loading && (
        <table className="admin-table">
          <thead>
            <tr>
              <th></th>
              <th>Titre</th>
              <th>Catégorie</th>
              <th>Statut</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {articles?.map((a) => (
              <tr key={a.id}>
                <td>
                  {a.image_couverture ? (
                    <img src={`${API_URL}${a.image_couverture}`} alt="" className="admin-table__thumb" />
                  ) : (
                    <div className="admin-table__thumb--empty" />
                  )}
                </td>
                <td>{a.titre}</td>
                <td>{a.categorie_nom || '—'}</td>
                <td>{a.statut}</td>
                <td className="admin-table__actions">
                  <button onClick={() => ouvrirEdition(a)}>Modifier</button>
                  <button onClick={() => handleDelete(a.id)}>Supprimer</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
